import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  getMessages,
  getMessageDetail,
  getCustomerAgents,
  getBalance,
  patchMessage,
} from '../lib/api';
import { formatDateTime, formatListActivityTime, buildTelHref, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  MessageSquare,
  Clock,
  FileText,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Search,
  Smartphone,
  Bot,
  User,
  Filter,
  MoreVertical,
  UserCheck,
  RotateCcw,
  List,
  LayoutList,
  Send,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { IntentSemanticsIcon, SentimentSemanticsIcon } from '../components/call/CallSemanticsIcon';
import { getApiError } from '../lib/api';
import {
  normalizeSubscriptionMessagesBalance as normalizeBalance,
  normalizeSubscriptionMessagesFromAgents as normalizeAgents,
  normalizeFacebookQuota,
  normalizeSmsQuotaForAgent,
  normalizeFacebookQuotaForAgent,
} from '../lib/usageMetrics';
import { QuotaStatCard } from '../components/QuotaStatCard';

const getMessageId = (row) => row?.message_id || row?.messageId || row?.id;

const getIntent = (row) =>
  row?.intent ?? row?.summary_details?.intent ?? row?.analysis?.intent;

const getCustomerSentiment = (row) =>
  row?.customer_sentiment ?? row?.customerSentiment ?? row?.summary_details?.customer_sentiment;

const getMessageFollowUpRequired = (row) => {
  const v =
    row?.follow_up_required ??
    row?.followUpRequired ??
    row?.summary_details?.follow_up_required ??
    row?.analysis?.follow_up_required;
  return v === true || v === 'true' || v === 1 || v === '1';
};

const getMessageListPhone = (row) =>
  row?.customer?.phone ||
  row?.from ||
  row?.customer_phone ||
  row?.customerPhone ||
  'Unknown number';

const getMessageListName = (row) =>
  row?.customer?.name || row?.customer_name || row?.customerName || '';

const channelLabel = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return 'Message';
  return s === 'sms' ? 'SMS' : s.charAt(0).toUpperCase() + s.slice(1);
};

function ChannelIcon({ channel, className }) {
  const c = String(channel || '').toLowerCase();
  if (c === 'sms') return <Smartphone className={className} aria-hidden />;
  return <MessageSquare className={className} aria-hidden />;
}

export function MessageLogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [agents, setAgents] = useState([]);
  const [balance, setBalance] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agentId') || 'all');
  const [markingMessageId, setMarkingMessageId] = useState(null);
  const [listView, setListView] = useState('simple');
  const limit = 20;

  useEffect(() => {
    const loadNav = async () => {
      try {
        const [agentsRes, balanceRes] = await Promise.all([
          getCustomerAgents({ limit: 200 }),
          getBalance(),
        ]);
        const list = Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : [];
        setAgents(list);
        setBalance(balanceRes?.data ?? null);
      } catch {
        setAgents([]);
        setBalance(null);
      }
    };
    loadNav();
  }, []);

  useEffect(() => {
    loadMessages();
  }, [page, agentFilter]);

  useEffect(() => {
    setPage(0);
  }, [agentFilter]);

  const normalizedBalance = useMemo(() => normalizeBalance(balance), [balance]);
  const normalizedAgents = useMemo(() => normalizeAgents(agents), [agents]);

  const messagingQuotas = useMemo(() => {
    if (agentFilter !== 'all') {
      const agent = agents.find((a) => a.agent_id === agentFilter);
      if (!agent) {
        return {
          sms: { remaining: 0, total: 0 },
          social: { remaining: 0, total: 0 },
        };
      }
      return {
        sms: normalizeSmsQuotaForAgent(agent),
        social: normalizeFacebookQuotaForAgent(agent),
      };
    }
    const hasAgentSubscriptionData =
      normalizedAgents.subscriptionRemainingMessages > 0 ||
      normalizedAgents.subscriptionUsedMessages > 0 ||
      normalizedAgents.subscriptionTotalMessages > 0;
    const smsRemaining = hasAgentSubscriptionData
      ? normalizedAgents.subscriptionRemainingMessages
      : normalizedBalance.subscriptionRemainingMessages;
    const smsTotal = hasAgentSubscriptionData
      ? normalizedAgents.subscriptionTotalMessages
      : normalizedBalance.subscriptionTotalMessages;
    return {
      sms: { remaining: smsRemaining, total: Math.max(smsTotal, smsRemaining) },
      social: normalizeFacebookQuota(balance, agents),
    };
  }, [agentFilter, agents, balance, normalizedBalance, normalizedAgents]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params = { limit, skip: page * limit };
      if (agentFilter !== 'all') {
        params.agent_id = agentFilter;
        params.agentId = agentFilter;
      }
      const response = await getMessages(params);
      setMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
      setTotal(Number(response?.data?.total) || 0);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const selectedAgentName =
    agentFilter === 'all' ? null : agents.find((a) => a.agent_id === agentFilter)?.name;

  const filteredMessages = messages.filter((row) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const id = getMessageId(row) || '';
    const preview = row.preview || row.ai_summary || '';
    const phone = row.customer?.phone || '';
    const name = row.customer?.name || '';
    return (
      id.toLowerCase().includes(search) ||
      preview.toLowerCase().includes(search) ||
      phone.toLowerCase().includes(search) ||
      name.toLowerCase().includes(search)
    );
  });

  const displayMessages = useMemo(() => {
    return [...filteredMessages].sort((a, b) => {
      const ac = Boolean(a.contacted);
      const bc = Boolean(b.contacted);
      if (ac !== bc) return ac ? 1 : -1;
      return 0;
    });
  }, [filteredMessages]);

  const handleSetMessageContacted = async (row, contacted, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const mid = getMessageId(row);
    if (!mid) return;
    setMarkingMessageId(mid);
    try {
      await patchMessage(mid, { contacted });
      toast.success(contacted ? 'Marked as contacted' : 'Marked as not contacted');
      await loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update message');
    } finally {
      setMarkingMessageId(null);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="message-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Messages</h1>
          <p className="text-slate-500 mt-1">
            SMS, WhatsApp, Facebook, and other channels in one place—AI summaries, sentiment, intent, and smart next steps
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          data-testid="message-toggle-filters"
        >
          <Filter className="w-4 h-4 mr-2" />
          {showFilters ? 'Hide filters' : 'Show filters'}
        </Button>
      </div>

      {showFilters && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Filters</p>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search ID, preview, name or phone…"
                className="h-9 pl-9 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="message-search-input"
              />
            </div>
            <div
              className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-slate-50 p-0.5 shrink-0 self-start sm:self-center"
              role="group"
              aria-label="List layout"
            >
              <Button
                type="button"
                variant={listView === 'simple' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2.5 gap-1 text-xs"
                onClick={() => setListView('simple')}
                data-testid="message-list-view-simple"
              >
                <List className="w-3.5 h-3.5" />
                Simple
              </Button>
              <Button
                type="button"
                variant={listView === 'detailed' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2.5 gap-1 text-xs"
                onClick={() => setListView('detailed')}
                data-testid="message-list-view-detailed"
              >
                <LayoutList className="w-3.5 h-3.5" />
                Detailed
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <QuotaStatCard
              title="SMS"
              icon={MessageSquare}
              iconWrapClass="bg-green-50 text-green-600"
              remaining={messagingQuotas.sms.remaining}
              total={messagingQuotas.sms.total}
              formatPrimary={(rem) => Math.max(0, Math.round(rem)).toLocaleString()}
              formatTotalLine={(tot) => `${Math.max(0, Math.round(tot)).toLocaleString()} total`}
              testId="messages-sms-quota-card"
              compact
            />
            <QuotaStatCard
              title="Social"
              icon={Send}
              iconWrapClass="bg-sky-50 text-sky-600"
              remaining={messagingQuotas.social.remaining}
              total={messagingQuotas.social.total}
              formatPrimary={(rem) => Math.max(0, Math.round(rem)).toLocaleString()}
              formatTotalLine={(tot) => `${Math.max(0, Math.round(tot)).toLocaleString()} total`}
              testId="messages-social-quota-card"
              compact
            />
          </div>

          <div className="w-full max-w-md">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Agent</label>
            <Select
              value={agentFilter}
              onValueChange={(value) => {
                setAgentFilter(value);
                const next = new URLSearchParams(searchParams);
                if (value === 'all') next.delete('agentId');
                else next.set('agentId', value);
                setSearchParams(next);
              }}
            >
              <SelectTrigger aria-label="Filter by agent" className="h-9 text-sm">
                <SelectValue placeholder="Filter by agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.agent_id} value={agent.agent_id}>
                    {agent.name || agent.agent_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {agentFilter !== 'all' && (
            <p className="text-xs text-slate-600">
              Filtered by agent:{' '}
              <span className="font-medium">{selectedAgentName || agentFilter}</span>
            </p>
          )}
        </div>
      )}

      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchTerm ? 'No matching messages' : 'No messages yet'}
              </h3>
              <p className="text-slate-500 max-w-sm mx-auto">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Conversations will appear here when customers message your agents.'}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {displayMessages.map((row, index) => {
                  const mid = getMessageId(row);
                  const intent = getIntent(row);
                  const sentiment = getCustomerSentiment(row);
                  const short = mid && mid.length > 8 ? mid.slice(-8) : mid || '—';
                  const listPhone = getMessageListPhone(row);
                  const listName = getMessageListName(row);
                  const actionRequired = getMessageFollowUpRequired(row);
                  const telHref = buildTelHref(listPhone);
                  const messagePath = mid ? `/messages/${encodeURIComponent(mid)}` : '/messages';
                  return (
                    <div
                      key={mid || `message-row-${index}`}
                      className={cn(
                        'flex items-stretch min-h-[72px]',
                        row.contacted && 'opacity-70 grayscale-[0.35] bg-slate-50/90',
                      )}
                      data-testid={mid ? `message-row-${mid}` : undefined}
                    >
                      {listView === 'simple' ? (
                        <div
                          role="link"
                          tabIndex={0}
                          className={cn(
                            'flex-1 min-w-0 p-4 hover:bg-slate-50 transition-colors flex cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
                            row.contacted && 'hover:bg-slate-100/80',
                          )}
                          onClick={() => mid && navigate(messagePath)}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && mid) {
                              e.preventDefault();
                              navigate(messagePath);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-3 w-full">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div
                                className="shrink-0 pt-0.5"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                {sentiment ? (
                                  <SentimentSemanticsIcon rawValue={sentiment} size={18} />
                                ) : (
                                  <span
                                    className={cn(
                                      'inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset bg-slate-100 text-slate-500 ring-slate-200',
                                      row.contacted && 'opacity-80',
                                    )}
                                    aria-hidden
                                  >
                                    <ChannelIcon channel={row.channel} className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                                  {telHref ? (
                                    <a
                                      href={telHref}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      className={cn(
                                        'text-base font-semibold tracking-tight text-blue-600 hover:underline shrink-0',
                                        row.contacted && 'text-blue-500/90',
                                      )}
                                    >
                                      {listPhone}
                                    </a>
                                  ) : (
                                    <span
                                      className={cn(
                                        'text-base font-semibold tracking-tight',
                                        row.contacted ? 'text-slate-600' : 'text-slate-900',
                                      )}
                                    >
                                      {listPhone}
                                    </span>
                                  )}
                                  {listName ? (
                                    <span className="text-sm text-slate-400 truncate">{listName}</span>
                                  ) : null}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {formatListActivityTime(row.created_at)}
                                </p>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                  {row.contacted && (
                                    <Badge variant="secondary" className="text-xs font-normal text-slate-600">
                                      Contacted
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {channelLabel(row.channel)}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 self-start pt-0.5">
                              {intent ? (
                                <span
                                  className="inline-flex"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <IntentSemanticsIcon rawValue={intent} size={16} />
                                </span>
                              ) : null}
                              <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={messagePath}
                          className={cn(
                            'flex-1 min-w-0 p-4 hover:bg-slate-50 transition-colors flex',
                            row.contacted && 'hover:bg-slate-100/80',
                          )}
                        >
                          <div className="flex items-start justify-between gap-4 w-full">
                            <>
                              <div className="flex items-start gap-4 flex-1 min-w-0">
                                <div
                                  className={cn(
                                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                                    row.contacted ? 'bg-slate-200' : 'bg-cyan-50',
                                  )}
                                >
                                  <ChannelIcon
                                    channel={row.channel}
                                    className={cn('w-5 h-5', row.contacted ? 'text-slate-500' : 'text-cyan-600')}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p
                                      className={cn(
                                        'font-medium',
                                        row.contacted ? 'text-slate-600' : 'text-slate-900',
                                      )}
                                    >
                                      Message #{short}
                                    </p>
                                    {row.contacted && (
                                      <Badge variant="secondary" className="text-xs font-normal text-slate-600">
                                        Contacted
                                      </Badge>
                                    )}
                                    <Badge variant={row.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                      {row.status || '—'}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                      {channelLabel(row.channel)}
                                    </Badge>
                                    {row.agent?.name && (
                                      <Badge variant="outline" className="text-xs">
                                        {row.agent.name}
                                      </Badge>
                                    )}
                                    {(intent || sentiment) && (
                                      <div
                                        className="flex items-center gap-1.5"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                      >
                                        <IntentSemanticsIcon rawValue={intent} size={15} />
                                        <SentimentSemanticsIcon rawValue={sentiment} size={15} />
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500 mb-2">{formatDateTime(row.created_at)}</p>
                                  {row.preview && (
                                    <div className="flex items-start gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                                      <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                                      <p className="text-sm text-slate-600 line-clamp-2">{row.preview}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                {actionRequired && (
                                  <Badge variant="destructive" className="text-xs">
                                    Follow-up
                                  </Badge>
                                )}
                                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
                              </div>
                            </>
                          </div>
                        </Link>
                      )}
                      <div
                        className="flex items-center border-l border-slate-100 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-full rounded-none px-3"
                              aria-label="Message actions"
                              onClick={(e) => e.preventDefault()}
                            >
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {row.contacted ? (
                              <DropdownMenuItem
                                disabled={markingMessageId === mid}
                                onClick={(e) => handleSetMessageContacted(row, false, e)}
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Mark as not contacted
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                disabled={markingMessageId === mid}
                                onClick={(e) => handleSetMessageContacted(row, true, e)}
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                Mark as contacted
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} messages
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function MessageDetailPage() {
  const { messageId: messageIdParam } = useParams();
  const messageId = messageIdParam ? decodeURIComponent(messageIdParam) : '';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [contactedUpdating, setContactedUpdating] = useState(false);

  useEffect(() => {
    loadMessage();
  }, [messageId]);

  const loadMessage = async () => {
    try {
      const response = await getMessageDetail(messageId, { suppressGlobalErrorToast: true });
      setMsg(response.data);
    } catch (error) {
      const normalized = getApiError(error);
      const status = normalized.status;
      if (status === 404) {
        toast.error('Message not found or not accessible');
      } else if (status === 502) {
        toast.error(normalized.detail || 'AI enrichment failed for this message');
      } else if (status === 503) {
        toast.error(normalized.detail || 'AI enrichment is not available');
      } else {
        toast.error('Failed to load message details');
      }
      navigate('/messages');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMessageContacted = async () => {
    if (!messageId) return;
    const next = !msg?.contacted;
    setContactedUpdating(true);
    try {
      await patchMessage(messageId, { contacted: next });
      toast.success(next ? 'Marked as contacted' : 'Marked as not contacted');
      await loadMessage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update message');
    } finally {
      setContactedUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="border-0 shadow-md">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!msg) {
    return null;
  }

  const id = getMessageId(msg);
  const short = id && id.length > 8 ? id.slice(-8) : id || '—';
  const intent = getIntent(msg);
  const sentiment = getCustomerSentiment(msg);
  const detail = msg.detail || null;
  const customer = msg.customer || {};
  const agent = msg.agent || {};

  const interactionMessageTotalRaw =
    msg.interaction_message_total ?? detail?.interaction_message_total;
  const interactionMessageTotal =
    interactionMessageTotalRaw !== undefined &&
    interactionMessageTotalRaw !== null &&
    interactionMessageTotalRaw !== ''
      ? Number(interactionMessageTotalRaw)
      : null;
  const interactionMessageTotalDisplay =
    interactionMessageTotal !== null && Number.isFinite(interactionMessageTotal)
      ? interactionMessageTotal.toLocaleString()
      : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="message-detail-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/messages')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-jakarta font-bold text-slate-900">Message #{short}</h1>
            <p className="text-slate-500">{formatDateTime(msg.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            disabled={contactedUpdating}
            onClick={handleToggleMessageContacted}
            className={msg.contacted ? 'text-slate-600' : ''}
          >
            {msg.contacted ? (
              <>
                <RotateCcw className="w-4 h-4 mr-2" />
                Mark as not contacted
              </>
            ) : (
              <>
                <UserCheck className="w-4 h-4 mr-2" />
                Mark as contacted
              </>
            )}
          </Button>
          {agent.agent_id && (
            <Button variant="outline" onClick={() => navigate(`/agents/${agent.agent_id}`)}>
              <Bot className="w-4 h-4 mr-2" />
              View Agent
            </Button>
          )}
        </div>
      </div>

      {(msg.channel || customer.name || customer.phone || customer.notes) && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              Customer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {msg.channel && (
              <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center shrink-0">
                  <ChannelIcon channel={msg.channel} className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Channel</p>
                  <p className="font-semibold text-slate-900">{channelLabel(msg.channel)}</p>
                </div>
              </div>
            )}
            {(customer.name || customer.phone || customer.notes) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customer.name && (
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="font-medium text-slate-900">{customer.name}</p>
                  </div>
                )}
                {customer.phone && (
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <a
                      href={`tel:${String(customer.phone).replace(/\s+/g, '')}`}
                      className="font-medium text-teal-700 hover:text-teal-800 hover:underline"
                    >
                      {customer.phone}
                    </a>
                  </div>
                )}
                {customer.notes && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-slate-500">Notes</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{customer.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {(msg.ai_summary || interactionMessageTotalDisplay) && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Agent summary
            </CardTitle>
            {interactionMessageTotalDisplay && (
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-semibold text-slate-900">{interactionMessageTotalDisplay}</span>
                {' '}
                messages in this interaction
              </p>
            )}
            {msg.ai_enriched_at && (
              <p className="text-xs text-slate-500 mt-1">Enriched {formatDateTime(msg.ai_enriched_at)}</p>
            )}
          </CardHeader>
          {msg.ai_summary && (
            <CardContent>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{msg.ai_summary}</p>
            </CardContent>
          )}
        </Card>
      )}

      {(intent || sentiment || msg.outcome != null || typeof msg.follow_up_required === 'boolean') && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Summary signals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(intent || sentiment) && (
              <div className="flex flex-wrap items-center gap-3">
                <IntentSemanticsIcon rawValue={intent} size={18} />
                <SentimentSemanticsIcon rawValue={sentiment} size={18} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {msg.outcome != null && String(msg.outcome).trim() !== '' && (
                <div>
                  <p className="text-xs text-slate-500">Outcome</p>
                  <p className="font-medium text-slate-900 break-words">{String(msg.outcome)}</p>
                </div>
              )}
              {typeof msg.follow_up_required === 'boolean' && (
                <div>
                  <p className="text-xs text-slate-500">Follow-up required</p>
                  <p className="font-medium text-slate-900">{msg.follow_up_required ? 'Yes' : 'No'}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {agent.agent_id && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs text-slate-500">Agent</p>
                <p className="font-semibold text-slate-900">{agent.name || agent.agent_id}</p>
                {agent.phone_number && (
                  <p className="text-sm text-slate-600 mt-1">{agent.phone_number}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">Agent ID: {agent.agent_id}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/agents/${agent.agent_id}`)}>
                Open Agent
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {detail?.full_summary && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg">Full summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{detail.full_summary}</p>
            {Array.isArray(detail.topics) && detail.topics.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {detail.topics.map((topic) => (
                  <Badge key={topic} variant="secondary">
                    {topic}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500">Message ID</p>
              <p className="font-mono text-slate-900 break-all">{id}</p>
            </div>
            {detail?.message_id && detail.message_id !== id && (
              <div>
                <p className="text-xs text-slate-500">Summary message ID</p>
                <p className="font-mono text-slate-900 break-all">{detail.message_id}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!msg.ai_summary && !detail?.full_summary && !interactionMessageTotalDisplay && (
        <Card className="border-0 shadow-md">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No summary available for this message yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
